// Starting frames for the reporting prototype. A report begins from one frame
// — the base table its op-chain (pipeline build) or ViewConfig (flat build) runs
// against. Today there are two, both mocked (no backend reads): `deal` (one row
// per deal) and `deal_activity` (one row per logged activity event).
//
// A FrameDef is the single source of truth for everything downstream that used
// to be hardcoded to the deal: the rows to query, the field roles the builder
// offers (group-by dims, numeric measures, date buckets, filters), the named
// measures, column labels, group ordering, and the trend date. The flat engine
// and builder read all of this from the frame, so neither knows about "deals"
// specifically.
import * as aq from 'arquero'
import { ACTIVITY_DATEF, MOCK_DEAL_ACTIVITY } from './mock-activity'
import { MOCK_CONTACTS } from './mock-contacts'
import { MOCK_DEALS, STAGE_ORDER } from './mock-data'
import { MOCK_OPPORTUNITIES, opportunityById } from './mock-opportunity'
import {
  CAT_DIMS,
  DATEF,
  FIELDS,
  materialize,
  NUMF,
  ROWS,
} from './pipeline-data'
import { FieldSchema, FrameId, Row } from './pipeline-types'
import { Metric } from './view-config'

export type { FrameId }

export interface FrameDef {
  id: string // FrameId for registered frames; synthetic string for dynamic joins
  /** Human label for the frame picker. */
  label: string
  /** One-line description of the grain (what a row represents). */
  description: string
  /** Materialized scalar rows for the pipeline engine (arrays dropped). */
  rows: Row[]
  /** Raw records for the flat engine (arrays/nulls preserved for list display). */
  records: Record<string, unknown>[]
  /** Inferred field schema (column name + type, distinct values for cat cols). */
  fields: FieldSchema[]
  /** ISO date columns — bucketable in groupby, offered as recency filters. */
  dateFields: string[]
  /** Numeric columns offered as aggregate measures (sum/avg/min/max) + filters. */
  numFields: string[]
  /** Categorical columns offered as groupby dimensions + `in` filters. */
  catDims: string[]
  /** Named measures the flat builder offers (count + curated sums/avgs). */
  measures: Metric[]
  /** Display labels per column (falls back to the raw field name). */
  labels: Record<string, string>
  /** All displayable/exportable columns in canonical order (column-picker source). */
  columns: string[]
  /** Default selected columns for a new list report. */
  defaultColumns: string[]
  /** Explicit group order for a categorical dimension (else metric-desc). */
  orderings?: Record<string, string[]>
  /** Date field driving the metric trend/sparkline (absent → trend disabled). */
  trendDate?: string
}

// ── deal frame ──────────────────────────────────────────────────────────────

/** Named measures the deal builder offers, in canonical order. */
const DEAL_MEASURES: Metric[] = [
  { agg: 'count', label: 'Deals' },
  { agg: 'sum', field: 'monthly_volume', label: 'Monthly Volume' },
  { agg: 'sum', field: 'annual_volume', label: 'Yearly Volume' },
  { agg: 'sum', field: 'monthly_revenue', label: 'Monthly Revenue' },
  { agg: 'sum', field: 'annual_revenue', label: 'Yearly Revenue' },
  { agg: 'avg', field: 'annual_revenue', label: 'Avg Yearly Revenue' },
  // Probability-weighted pipeline (potential × stage win rate) — the forecast.
  { agg: 'sum', field: 'forecasted_monthly_volume', label: 'Forecasted Monthly Volume' }, // prettier-ignore
  { agg: 'sum', field: 'forecasted_annual_revenue', label: 'Forecasted Yearly Revenue' }, // prettier-ignore
  { agg: 'avg', field: 'win_probability', label: 'Avg Win Probability' },
  { agg: 'avg', field: 'days_in_stage', label: 'Avg Days in Stage' },
  // avg of is_won (1 won / 0 lost / null open) = Won ÷ closed = win rate.
  { agg: 'avg', field: 'is_won', label: 'Win Rate' },
]

const DEAL_LABELS: Record<string, string> = {
  deal_id: 'Deal ID',
  deal_name: 'Deal',
  account_owner: 'Account Owner',
  company_name: 'Company',
  category: 'Type',
  sub_category: 'Category',
  product: 'Product',
  sales_stage: 'Sales Stage',
  days_in_stage: 'Days in Stage',
  days_since_update: 'Days Since Update',
  is_won: 'Won',
  projected_close_date: 'Projected Close',
  actual_close_date: 'Actual Close',
  win_probability: 'Win Probability',
  created: 'Created',
  note_count: 'Notes',
  contact_count: 'Contacts',
  distributor_names: 'Distributors',
  gpo_names: 'GPOs',
  monthly_volume: 'Monthly Volume',
  annual_volume: 'Yearly Volume',
  monthly_revenue: 'Monthly Revenue',
  annual_revenue: 'Yearly Revenue',
  forecasted_monthly_volume: 'Forecasted Monthly Volume',
  forecasted_annual_revenue: 'Forecasted Yearly Revenue',
}

/** Deal columns users may show or export, in canonical order. */
const DEAL_COLUMNS: string[] = [
  'deal_id',
  'deal_name',
  'account_owner',
  'company_name',
  'category',
  'sub_category',
  'product',
  'sales_stage',
  'days_in_stage',
  'days_since_update',
  'projected_close_date',
  'actual_close_date',
  'win_probability',
  'created',
  'note_count',
  'contact_count',
  'distributor_names',
  'gpo_names',
  'monthly_volume',
  'annual_volume',
  'monthly_revenue',
  'annual_revenue',
  'forecasted_monthly_volume',
  'forecasted_annual_revenue',
]

const DEAL_DEFAULT_COLUMNS: string[] = [
  'account_owner',
  'company_name',
  'category',
  'product',
  'sales_stage',
  'created',
  'monthly_volume',
  'annual_revenue',
]

// Deal columns a JOIN leads with — a compact identity set (not all 8) so the
// joined-source columns stay in the visible width of the preview / table.
const DEAL_JOIN_DEFAULT_COLUMNS: string[] = [
  'deal_name',
  'sales_stage',
  'annual_revenue',
]

// ── deal_opportunity frame (deal LEFT JOIN opportunity) ───────────────────────

const DEAL_OPP_RECORDS = MOCK_DEALS.map((deal) => {
  const opp = opportunityById.get(deal.opportunity_id)
  return {
    ...deal,
    opportunity_name: opp?.name ?? null,
    opportunity_domain: opp?.domain ?? null,
    meals_per_day: opp?.meals_per_day ?? null,
  }
})

const DEAL_OPP_LABELS: Record<string, string> = {
  ...DEAL_LABELS,
  opportunity_name: 'Opportunity',
  opportunity_domain: 'Domain',
  meals_per_day: 'Meals / Day',
}

const DEAL_OPP_COLUMNS: string[] = [
  ...DEAL_COLUMNS,
  'opportunity_name',
  'opportunity_domain',
  'meals_per_day',
]

const DEAL_OPP_DEFAULT_COLUMNS: string[] = [
  'opportunity_name',
  'opportunity_domain',
  'meals_per_day',
  'company_name',
  'sales_stage',
  'annual_revenue',
]

const dealOpp = materialize(DEAL_OPP_RECORDS, DATEF)

// ── deal_activity frame ───────────────────────────────────────────────────────

const ACTIVITY_LABELS: Record<string, string> = {
  id: 'ID',
  company_id: 'Company ID',
  deal_id: 'Deal ID',
  deal_name: 'Deal',
  owner: 'Owner',
  owner_id: 'Owner ID',
  created_at: 'Date',
  event_type: 'Activity',
}

const ACTIVITY_COLUMNS: string[] = [
  'created_at',
  'event_type',
  'deal_name',
  'owner',
  'deal_id',
]

/** Activity events in lifecycle order — reads better than count-desc. */
const ACTIVITY_EVENT_ORDER: string[] = [
  'deal_created',
  'added_note',
  'added_contact',
  'had_call',
  'had_meeting',
  'added_doc',
  'updated_sales_stage',
  'deal_won',
  'deal_lost',
  'churned',
]

const activity = materialize(MOCK_DEAL_ACTIVITY, ACTIVITY_DATEF)

// ── contacts ──────────────────────────────────────────────────────────────────
// Contacts hang off a chain (opportunity); they relate to deals only through that
// shared chain. A deals×contacts report JOINS on the chain key (a deal's
// opportunity == a contact's opportunity_id) → one row per matched (deal, contact)
// pair, the same flatten join used for deal×activity. That fanout repeats each
// deal's values once per contact in its chain, so additive measures double-count
// within a chain — use count(distinct deal_id) / per-row reads, not sum.
//
// The standalone contact frame (contacts picked alone) is one row per contact,
// joined N:1 to its opportunity for context (no fanout).
const CONTACT_RECORDS = MOCK_CONTACTS.map((c) => {
  const opp = MOCK_OPPORTUNITIES.find((o) => o.id === c.opportunity_id)
  return {
    contact_id: c.contact_id,
    contact_name: c.name,
    title: c.title,
    department: c.department,
    opportunity_name: opp?.name ?? null,
  }
})

const CONTACT_LABELS: Record<string, string> = {
  contact_id: 'Contact ID',
  contact_name: 'Contact',
  title: 'Title',
  department: 'Department',
  opportunity_name: 'Chain',
}

const contact = materialize(CONTACT_RECORDS, [])

/** Standalone "Contacts" frame — one row per contact. */
const CONTACT_FRAME: FrameDef = {
  id: 'contacts',
  label: 'Contacts',
  description: 'One row per contact.',
  rows: contact.rows,
  records: CONTACT_RECORDS,
  fields: contact.fields,
  dateFields: [],
  numFields: [],
  catDims: ['title', 'department', 'opportunity_name'],
  measures: [{ agg: 'count', label: 'Contacts' }],
  labels: CONTACT_LABELS,
  columns: ['contact_name', 'title', 'department', 'opportunity_name'],
  defaultColumns: ['contact_name', 'title', 'department', 'opportunity_name'],
}

export const FRAMES: Record<FrameId, FrameDef> = {
  deal_opportunity: {
    id: 'deal_opportunity',
    label: 'Deals + Opportunities',
    description: 'One row per deal, left-joined to its opportunity account.',
    rows: dealOpp.rows,
    records: DEAL_OPP_RECORDS,
    fields: dealOpp.fields,
    dateFields: DATEF,
    numFields: [...NUMF, 'meals_per_day'],
    catDims: [...CAT_DIMS, 'opportunity_domain'],
    measures: [
      ...DEAL_MEASURES,
      { agg: 'sum', field: 'meals_per_day', label: 'Total Meals/Day' },
      { agg: 'avg', field: 'meals_per_day', label: 'Avg Meals/Day' },
    ],
    labels: DEAL_OPP_LABELS,
    columns: DEAL_OPP_COLUMNS,
    defaultColumns: DEAL_OPP_DEFAULT_COLUMNS,
    orderings: { sales_stage: STAGE_ORDER },
    trendDate: 'created',
  },
  deal: {
    id: 'deal',
    label: 'Deals',
    description: 'One row per deal — the deal_reporting view.',
    rows: ROWS,
    records: MOCK_DEALS,
    fields: FIELDS,
    dateFields: DATEF,
    numFields: NUMF,
    catDims: CAT_DIMS,
    measures: DEAL_MEASURES,
    labels: DEAL_LABELS,
    columns: DEAL_COLUMNS,
    defaultColumns: DEAL_DEFAULT_COLUMNS,
    orderings: { sales_stage: STAGE_ORDER },
    trendDate: 'created',
  },
  deal_activity: {
    id: 'deal_activity',
    label: 'Deal activity',
    description: 'One row per logged activity event on a deal.',
    rows: activity.rows,
    records: MOCK_DEAL_ACTIVITY,
    fields: activity.fields,
    dateFields: ACTIVITY_DATEF,
    // Activities are counted, not summed — there is no natural numeric measure.
    numFields: [],
    catDims: ['event_type', 'owner', 'deal_name'],
    measures: [{ agg: 'count', label: 'Activities' }],
    labels: ACTIVITY_LABELS,
    columns: ACTIVITY_COLUMNS,
    defaultColumns: ['created_at', 'event_type', 'deal_name', 'owner'],
    orderings: { event_type: ACTIVITY_EVENT_ORDER },
    trendDate: 'created_at',
  },
}

/** Frames in picker order. */
export const FRAME_LIST: FrameDef[] = [
  FRAMES.deal,
  FRAMES.deal_opportunity,
  FRAMES.deal_activity,
]

/** The frame a new report starts from. */
export const DEFAULT_FRAME: FrameId = 'deal'

/** The frame a ViewConfig runs against (defaults to the registry default). */
export const frameOf = (frame: FrameId = DEFAULT_FRAME): FrameDef =>
  FRAMES[frame]

/**
 * Resolve the frame a saved config runs against. Prefers the persisted `sources`
 * set (rebuilds multi-source joins via `frameForSources`, so a saved Deals +
 * Contacts report reopens as the join, not the collapsed `frame`). Falls back to
 * the single registered `frame` for configs saved before `sources` existed.
 */
export const frameForConfig = (config: {
  sources?: SourceId[]
  frame?: FrameId
}): FrameDef =>
  (config.sources?.length ? frameForSources(config.sources) : null) ??
  frameOf(config.frame)

// ── source picker ─────────────────────────────────────────────────────────────

export type SourceId = 'deals' | 'opportunities' | 'activity' | 'contacts'

export interface SourceDef {
  id: SourceId
  label: string
  description: string
  count: number
}

export const SOURCES: SourceDef[] = [
  {
    id: 'deals',
    label: 'Deals',
    description: 'One row per deal',
    count: MOCK_DEALS.length,
  },
  {
    id: 'opportunities',
    label: 'Opportunities',
    description: 'Operator accounts',
    count: MOCK_OPPORTUNITIES.length,
  },
  {
    id: 'activity',
    label: 'Deal Activity',
    description: 'One row per logged activity',
    count: MOCK_DEAL_ACTIVITY.length,
  },
  {
    id: 'contacts',
    label: 'Contacts',
    description: 'One row per contact (joins to deals by chain)',
    count: MOCK_CONTACTS.length,
  },
]

// ── join source tables ────────────────────────────────────────────────────────
// Each source projected to its join-ready columns: a shared key (opportunity_id
// for the deal/opp/contact chain, deal_id for activity) plus the fields that
// source contributes. arquero natural-joins on the shared key. `name` → contact_name
// avoids colliding with the deal's company_name. Built once at module load.
const OPP_JOIN_TABLE = MOCK_OPPORTUNITIES.map((o) => ({
  opportunity_id: o.id,
  opportunity_name: o.name,
  opportunity_domain: o.domain,
  meals_per_day: o.meals_per_day,
}))

const CONTACT_JOIN_TABLE = MOCK_CONTACTS.map((c) => ({
  opportunity_id: c.opportunity_id,
  contact_id: c.contact_id,
  contact_name: c.name,
  title: c.title,
  department: c.department,
}))

const ACTIVITY_JOIN_TABLE = MOCK_DEAL_ACTIVITY.map((a) => ({
  deal_id: a.deal_id,
  event_type: a.event_type,
  activity_date: a.created_at,
}))

/**
 * Inner-join the selected sources into flat records via arquero. The chain hub
 * is `opportunity_id` (deals, opportunities, contacts all carry it); activity
 * joins on `deal_id`. Anchor is deals when present, else opportunities (the only
 * no-deals multi-source combo that reaches here is opportunities + contacts).
 * activity + contacts is never both-selected (blocked upstream as a meaningless
 * contact×activity cartesian), so at most one fanout source attaches.
 */
function joinedRecords(selected: SourceId[]): Record<string, unknown>[] {
  const has = (s: SourceId) => selected.includes(s)
  let t = aq.from(has('deals') ? MOCK_DEALS : OPP_JOIN_TABLE)
  if (has('deals') && has('opportunities'))
    t = t.join(aq.from(OPP_JOIN_TABLE), 'opportunity_id')
  if (has('contacts')) t = t.join(aq.from(CONTACT_JOIN_TABLE), 'opportunity_id')
  if (has('activity')) t = t.join(aq.from(ACTIVITY_JOIN_TABLE), 'deal_id')
  return t.objects() as Record<string, unknown>[]
}

// Metadata each source contributes to a JOIN (different from standalone frames).
// Standalone single-source selection returns the existing pre-built frames below.
const JOIN_META: Record<
  SourceId,
  {
    labels: Record<string, string>
    catDims: string[]
    numFields: string[]
    columns: string[]
    defaultColumns: string[]
    dateFields: string[]
    orderings: Record<string, string[]>
    measures: Metric[]
  }
> = {
  deals: {
    labels: DEAL_LABELS,
    catDims: CAT_DIMS,
    numFields: NUMF,
    columns: DEAL_COLUMNS,
    defaultColumns: DEAL_JOIN_DEFAULT_COLUMNS,
    dateFields: DATEF,
    orderings: { sales_stage: STAGE_ORDER },
    measures: DEAL_MEASURES,
  },
  opportunities: {
    labels: {
      opportunity_name: 'Opportunity',
      opportunity_domain: 'Domain',
      meals_per_day: 'Meals / Day',
    },
    catDims: ['opportunity_domain'],
    numFields: ['meals_per_day'],
    columns: ['opportunity_name', 'opportunity_domain', 'meals_per_day'],
    defaultColumns: ['opportunity_name', 'opportunity_domain', 'meals_per_day'],
    dateFields: [],
    orderings: {},
    measures: [
      { agg: 'sum', field: 'meals_per_day', label: 'Total Meals/Day' },
      { agg: 'avg', field: 'meals_per_day', label: 'Avg Meals/Day' },
    ],
  },
  activity: {
    labels: { event_type: 'Activity Type', activity_date: 'Activity Date' },
    catDims: ['event_type'],
    numFields: [],
    columns: ['activity_date', 'event_type'],
    defaultColumns: ['activity_date', 'event_type'],
    dateFields: ['activity_date'],
    orderings: { event_type: ACTIVITY_EVENT_ORDER },
    measures: [{ agg: 'count', label: 'Activities' }],
  },
  contacts: {
    labels: {
      contact_id: 'Contact ID',
      contact_name: 'Contact',
      title: 'Title',
      department: 'Department',
    },
    catDims: ['contact_name', 'title', 'department'],
    numFields: [],
    columns: ['contact_name', 'title', 'department'],
    defaultColumns: ['contact_name', 'title', 'department'],
    dateFields: [],
    orderings: {},
    // distinct, not count: rows are (deal,contact) pairs, so count inflates with
    // fanout. count(distinct contact_id) is the true contact count per group.
    measures: [{ agg: 'distinct', field: 'contact_id', label: 'Contacts' }],
  },
}

/**
 * Build a FrameDef for a set of selected source IDs.
 * Single source → returns the pre-built registered frame.
 * Multi-source → inner joins the tables and merges metadata.
 * Returns null if the combination has no valid join path.
 */
export function frameForSources(selected: SourceId[]): FrameDef | null {
  if (selected.length === 0) return null

  const has = (s: SourceId) => selected.includes(s)

  // Joining two fanout children of the same chain (activity AND contacts) is a
  // meaningless contact×activity cartesian — block it.
  if (has('contacts') && has('activity')) return null

  // Activity only carries deal_id, so a join needs Deals to reach the chain — it
  // can't join opportunities without Deals. (Activity alone, length 1, is fine —
  // handled by the single-source path below.)
  if (has('activity') && !has('deals') && selected.length > 1) return null

  // Single-source: return the registered frame directly.
  if (selected.length === 1) {
    if (has('deals')) return FRAMES.deal
    if (has('activity')) return FRAMES.deal_activity
    if (has('contacts')) return CONTACT_FRAME
    // Standalone opportunity frame
    const oppRecords = MOCK_OPPORTUNITIES.map((o) => ({
      opportunity_name: o.name,
      opportunity_domain: o.domain,
      meals_per_day: o.meals_per_day,
    }))
    const oppMat = materialize(oppRecords, [])
    return {
      id: 'opportunities',
      label: 'Opportunities',
      description: 'One row per operator account',
      rows: oppMat.rows,
      records: oppRecords,
      fields: oppMat.fields,
      dateFields: [],
      numFields: ['meals_per_day'],
      catDims: ['opportunity_domain'],
      measures: [
        { agg: 'count', label: 'Opportunities' },
        { agg: 'sum', field: 'meals_per_day', label: 'Total Meals/Day' },
        { agg: 'avg', field: 'meals_per_day', label: 'Avg Meals/Day' },
      ],
      labels: JOIN_META.opportunities.labels,
      columns: JOIN_META.opportunities.columns,
      defaultColumns: JOIN_META.opportunities.defaultColumns,
    }
  }

  // Inner-join the selected sources on the chain keys (arquero).
  const records = joinedRecords(selected)

  // Merge metadata in a canonical source order (deals/opportunities lead), not
  // click order, so the anchor's columns always lead the preview / column picker
  // regardless of which source the user selected first.
  const SOURCE_ORDER: SourceId[] = [
    'deals',
    'opportunities',
    'contacts',
    'activity',
  ]
  const ordered = [...selected].sort(
    (a, b) => SOURCE_ORDER.indexOf(a) - SOURCE_ORDER.indexOf(b)
  )
  const metas = ordered.map((s) => JOIN_META[s])
  const merged = {
    labels: Object.assign({}, ...metas.map((m) => m.labels)) as Record<
      string,
      string
    >,
    catDims: [...new Set(metas.flatMap((m) => m.catDims))],
    numFields: [...new Set(metas.flatMap((m) => m.numFields))],
    columns: [...new Set(metas.flatMap((m) => m.columns))],
    defaultColumns: [...new Set(metas.flatMap((m) => m.defaultColumns))],
    dateFields: [...new Set(metas.flatMap((m) => m.dateFields))],
    orderings: Object.assign({}, ...metas.map((m) => m.orderings)) as Record<
      string,
      string[]
    >,
    measures: metas.flatMap((m) => m.measures),
  }

  // Contacts fans deals out to one row per (deal, contact) pair, so any additive
  // deal/opportunity measure (sum/avg) counts each deal once per contact — a
  // silent N× inflation. There is no correct additive rollup over the fanned
  // grain, so drop sum/avg and count deals distinctly instead. (Mirrors the
  // `distinct` treatment already on the contacts measure; contacts+activity is
  // blocked above, so contacts is the only fanout source that reaches here.)
  if (has('contacts') && has('deals')) {
    merged.measures = merged.measures
      .filter((m) => m.agg !== 'sum' && m.agg !== 'avg')
      .map((m) =>
        m.agg === 'count'
          ? { agg: 'distinct' as const, field: 'deal_id', label: m.label }
          : m
      )
  }

  const label = ordered
    .map(
      (s) =>
        ({
          deals: 'Deals',
          opportunities: 'Opportunities',
          activity: 'Deal Activity',
          contacts: 'Contacts',
        })[s]
    )
    .join(' + ')

  const mat = materialize(records, merged.dateFields)

  return {
    id: selected.sort().join('+'),
    label,
    description: `${records.length.toLocaleString()} rows · inner join`,
    rows: mat.rows,
    records,
    fields: mat.fields,
    dateFields: merged.dateFields,
    numFields: merged.numFields,
    catDims: merged.catDims,
    measures: merged.measures,
    labels: merged.labels,
    columns: merged.columns,
    defaultColumns: merged.defaultColumns,
    orderings: merged.orderings,
  }
}
